import React, { useEffect, useMemo, useState } from 'react';
import './ArticleFeed.css';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { TArticlePagination } from '../../types/shared/Pagination';
import { Article } from '../../types/shared/Article';
import { shortenText } from '../../utils/cob-utils';
import { Section, PhotoCard, TextLine } from '../../importCL';
import Pagination from '../Pagination/Pagination';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import withAsyncImport from '../../withAsyncImport';
import { useWidthContext } from '../../providers/Width';
import axios, { AxiosResponse } from 'axios';
import { articleResultsUrl, isRecord, readArticleFilters, TArticleFilterOptions } from '../ArticleFeedFilter/articleFilters';

const ArticleFeedFilter = withAsyncImport(() => import('../ArticleFeedFilter/ArticleFeedFilter'));
type TArticleFeedProps = MappedComponentProperties & {
    filterOptions?: TArticleFilterOptions;
    resultsHeading?: string;
    noResultsHeading?: string;
    noResultsMessage?: string;
    noResultsArticles?: Article[];
};

const parseArticles = (value: unknown): Article[] => {
    if (!Array.isArray(value)) {
        throw new Error('Invalid articles response');
    }
    return value.map((item: unknown): Article => {
        if (!isRecord(item) || !['title', 'previewDescription', 'pagePath', 'imagePath', 'imageAlt']
            .every((key: string): boolean => item[key] === undefined || item[key] === null || typeof item[key] === 'string')
            || (item.date !== undefined && item.date !== null && typeof item.date !== 'string' && typeof item.date !== 'number' && !(item.date instanceof Date))) {
            throw new Error('Invalid article');
        }
        return {
            title: typeof item.title === 'string' ? item.title : '',
            previewDescription: typeof item.previewDescription === 'string' ? item.previewDescription : '',
            pagePath: typeof item.pagePath === 'string' ? item.pagePath : '',
            imagePath: typeof item.imagePath === 'string' ? item.imagePath : '',
            imageAlt: typeof item.imageAlt === 'string' ? item.imageAlt : '',
            date: typeof item.date === 'string' || typeof item.date === 'number' ? new Date(item.date) : item.date instanceof Date ? item.date : undefined,
        };
    });
};

const ArticleFeed: React.FunctionComponent<TArticleFeedProps> = (props: TArticleFeedProps): JSX.Element => {
    const filters: URLSearchParams = readArticleFilters(typeof window === 'undefined' ? '' : window.location.search);
    const filterQuery = filters.toString();
    const isFiltered = filterQuery.length > 0;
    const requestedPage = typeof window === 'undefined' ? 1 : Number(new URLSearchParams(window.location.search).get('page') || 1);
    const pageNumber = Number.isSafeInteger(requestedPage) && requestedPage > 0 && requestedPage <= 357913941 ? requestedPage : 1;
    const [resultStatus, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const status = isFiltered ? resultStatus : 'ready';
    const [results, setPage] = useState<TArticlePagination>({});
    const metadata = PathUtils.getMetaPropertyValue('articleFeedData') || '{}';
    const originalPage: TArticlePagination = useMemo((): TArticlePagination => {
        const data: unknown = JSON.parse(metadata);
        if (!isRecord(data)) {
            return {};
        }
        return {
            pageNumber: typeof data.currentPageNumber === 'number' ? data.currentPageNumber : 1,
            lastPageNumber: typeof data.lastPageNumber === 'number' ? data.lastPageNumber : 0,
            topArticles: parseArticles(data.topArticles || []),
            bottomArticles: parseArticles(data.bottomArticles || []),
            baseUrl: typeof data.baseUrl === 'string' ? data.baseUrl : '',
        };
    }, [metadata]);

    const page: TArticlePagination = isFiltered ? results : originalPage;
    const isEmptyResult = isFiltered && status === 'ready' && !page.topArticles?.length;
    const originalArticles: Article[] = [...(originalPage.topArticles || []), ...(originalPage.bottomArticles || [])];
    const authoredFallback: Article[] = parseArticles(props.noResultsArticles || []);
   const fallbackArticles: Article[] =
    authoredFallback.length > 0 ? authoredFallback : originalArticles;
    const displayedArticles: Article[] = isEmptyResult
    ? fallbackArticles.slice(0, 6)
    : [...(page.topArticles || []), ...(page.bottomArticles || [])];
    const topArticles: Article[] = displayedArticles.slice(0, 3);
    const bottomArticles: Article[] = displayedArticles.slice(3);
    const { width } = useWidthContext();

    useEffect(() => {
        if (!isFiltered) {
            return;
        }
        let active = true;
        const loadResults = async (): Promise<void> => {
            setStatus('loading');
            try {
                const query: URLSearchParams = new URLSearchParams(filterQuery);
                query.set('limit', '6');
                query.set('offset', String((pageNumber - 1) * 6));
                const response: AxiosResponse<unknown> = await axios.get<unknown>(`/services/articles?${query.toString()}`);
                const data: unknown = response.data;
                if (!isRecord(data) || !isRecord(data.metadata) || typeof data.metadata.hasNext !== 'boolean'
                    || typeof data.metadata.guessLimit !== 'number' || !Number.isSafeInteger(data.metadata.guessLimit)
                    || data.metadata.guessLimit < 0) {
                    throw new Error('Invalid article results');
                }
                const articles: Article[] = parseArticles(data.articles).slice(0, 6);
                // The servlet counts up to 30 matches by default; hasNext also supports pages beyond that bound.
                const lastPageNumber = Math.max(Math.ceil(data.metadata.guessLimit / 6), pageNumber + (data.metadata.hasNext ? 1 : 0));
                if (active) {
                    setPage({ pageNumber, lastPageNumber, topArticles: articles, bottomArticles: [] });
                    setStatus('ready');
                }
            } catch (error: unknown) {
                if (active) {
                    setStatus('error');
                }
            }
        };
        void loadResults();
        return (): void => {
            active = false;
        };
    }, [filterQuery, isFiltered, pageNumber]);

    function generateContent(): JSX.Element {
        return (
            <Section className ='!py-8'>
                <div className='flex justify-between md:flex-col pb-8'>
                    <TextLine variant='author' tag='h3'
                        textClass={`flex items-center ${width > 768 ? 'text-[32px]':'text-[24px]'}`}
                        authorClass='h-[90%]'>
                        {isEmptyResult ? props.noResultsHeading || 'No articles match your filters'
                            : isFiltered ? props.resultsHeading || 'Article results' : 'Articles'}
                    </TextLine>
                    <ArticleFeedFilter filterOptions={props.filterOptions} />
                </div>
                {status === 'loading' && <p role='status'>Loading articles…</p>}
                {status === 'error' && <p role='alert'>Unable to load articles. Please reload the page to try again.</p>}
                {isEmptyResult && props.noResultsMessage && <p role='status'>{props.noResultsMessage}</p>}
                {status === 'ready' && <>
                    <div className='flex md:flex-col md:items-center'>
                        {topArticles?.map((topArticle: Article, index: number): JSX.Element => {
                            return (
                                <PhotoCard
                                    titleTag='h4'
                                    className='ArticleFeed__photoCard p-6'
                                    cardContentClass='!m-0' key={index}
                                    tagClass={`flex flex-col justify-between border border-solid border-[#BEBEBE] w-1/3 mb-6 md:w-full ${index===1?'ml-6 mr-6':''}`}
                                    anchorText='Read more'
                                    showAnchorMobile
                                    shadowless
                                    cardContent={{
                                        helperHeader: topArticle.date?.toDateString() || '',
                                        title: topArticle.title,
                                        description: shortenText(topArticle.previewDescription || ''),
                                        image: `${topArticle.imagePath}?imwidth=360`,
                                        imageAltText: topArticle.imageAlt,
                                        link: topArticle.pagePath,
                                        target: '_self',
                                    }}
                                />
                            );
                        })}
                    </div>
                    <div className='flex md:flex-col md:items-center'>
                        {bottomArticles?.map((bottomArticle: Article, index: number): JSX.Element => {
                            return (
                                <PhotoCard
                                    titleTag='h4'
                                    className='ArticleFeed__photoCard p-6'
                                    cardContentClass='!m-0'
                                    key={index}
                                    tagClass={`flex flex-col justify-between border border-solid border-[#BEBEBE] w-1/3 mb-6 md:w-full ${index===1?'ml-6 mr-6':''}`}
                                    anchorText='Read more'
                                    showAnchorMobile
                                    shadowless
                                    cardContent={{
                                        helperHeader: bottomArticle.date?.toDateString() || '',
                                        title: bottomArticle.title,
                                        description: shortenText(bottomArticle.previewDescription || ''),
                                        image: `${bottomArticle.imagePath}?imwidth=360`,
                                        imageAltText: bottomArticle.imageAlt,
                                        link: bottomArticle.pagePath,
                                        target: '_self',
                                    }}
                                />
                            );
                        })}
                    </div>
                </>}
            </Section>

        );
    }

    return (
        <div id='article-feed'>
            {generateContent()}
            {status === 'ready' && (!isEmptyResult || pageNumber > 1) && <Pagination {...page}
                buildPageUrl={isFiltered ? (number: number): string => articleResultsUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`, filters, number) : undefined} />}
        </div>
    );
};

export default ArticleFeed;
